import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, Link } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import PreviewOrchestrator from "../preview/PreviewOrchestrator";
import { PROJECT_PAGE_TYPE_TO_LAYOUT } from "../../utils/esmConfig";
import { pushBreadcrumb } from "../../store/slices/navigationSlice";
import {
  Alert,
  Spin,
  Tabs,
  Badge,
  Typography,
  Button,
  Table,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  renderGrid,
  setByPathCloned,
  buildMinimalPatch,
  getByPath,
} from "../common/FieldRenderer";
import {
  ChevronDown,
  ChevronRight,
  Edit,
  Eye,
  EyeOff,
  Copy,
  Zap,
  Save,
} from "lucide-react";
import { useRecents } from "../../hooks/useRecents";
import { PO_PICKLIST_LABELS } from "../../config/projectObjectiveFields";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_OBJECTIVE } from "../../config/sobjectNames";
import {
  buildInfoFieldsForDetails,
  buildRequirementsFieldsForDetails,
  buildFunnelFieldsForDetails,
  buildTargetsFieldsForDetails,
  buildDiversityFieldsForDetails,
  buildStatsFieldsForDetails,
  buildRatesFieldsForDetails,
  buildSysFieldsForDetails,
} from "./columnsConfig";
import { validateProjectObjectiveEditForm } from "../../config/projectObjectiveEdit.validation";
import CloneProjectObjectiveModal from "./CloneProjectObjectiveModal";
import ProjectPagesSection from "../sections/ProjectPagesSection";

const ProjectObjectiveDetails = ({
  projectObjectiveId: projectObjectiveIdProp,
  setObjectiveMetadata,
  isEditing: isEditingProp,
  setIsEditing: setIsEditingProp,
  showCloneModal: showCloneModalProp,
  setShowCloneModal: setShowCloneModalProp,
  showPreview: showPreviewProp,
  setShowPreview: setShowPreviewProp,
  onHeaderStateChange,
  onRegisterHandlers,
}) => {
  const { projectObjective, pageUtils, http } = useServices();
  const { projectObjectiveId: projectObjectiveIdFromParams } = useParams();
  const projectObjectiveId =
    projectObjectiveIdProp ?? projectObjectiveIdFromParams;
  const dispatch = useDispatch();
  const location = useLocation();

  const isControlled =
    typeof onRegisterHandlers === "function" &&
    setIsEditingProp != null &&
    setShowCloneModalProp != null &&
    setShowPreviewProp != null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [showPreviewLocal, setShowPreviewLocal] = useState(false);
  const [showCloneModalLocal, setShowCloneModalLocal] = useState(false);
  const isEditing = isControlled ? isEditingProp : isEditingLocal;
  const setIsEditing = isControlled ? setIsEditingProp : setIsEditingLocal;
  const showPreview = isControlled ? showPreviewProp : showPreviewLocal;
  const setShowPreview = isControlled
    ? setShowPreviewProp
    : setShowPreviewLocal;
  const showCloneModal = isControlled
    ? showCloneModalProp
    : showCloneModalLocal;
  const setShowCloneModal = isControlled
    ? setShowCloneModalProp
    : setShowCloneModalLocal;

  const [previewPageType, setPreviewPageType] = useState(
    "Project Pre-Qualification",
  );

  const [selectedPreviewType, setSelectedPreviewType] = useState(
    PROJECT_PAGE_TYPE_TO_LAYOUT["Project Pre-Qualification"],
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewPayload, setPreviewPayload] = useState(null);
  const [previewWidth, setPreviewWidth] = useState(50); // Percentage of available width
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const recordedIdRef = React.useRef(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [hideValidationSuccess, setHideValidationSuccess] = useState(false);
  const breadcrumbs = useSelector((state) => state.navigation.breadcrumbs);

  // Handle preview resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left - 20;
      const newWidth = Math.min(
        Math.max((mouseX / containerWidth) * 100, 20),
        66.67,
      ); // Max 2/3 (66.67%)

      setPreviewWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!projectObjectiveId) return;
      setData(null);
      setLoading(true);
      setError("");
      try {
        const record =
          await projectObjective.fetchProjectObjective(projectObjectiveId);
        if (!ignore) setData(record);
      } catch (e) {
        if (!ignore) setError(e?.message || "Failed to load PO details");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [projectObjectiveId, projectObjective]);

  // Push breadcrumb when PO loads
  useEffect(() => {
    const id = data?.id || projectObjectiveId;
    const provided = location?.state?.crumbLabel;
    const fallbackName = data?.info?.name;
    const name = provided || fallbackName;
    if (id && name) {
      if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        dispatch(pushBreadcrumb({ label: "Home", path: "/", type: "home" }));
      }
      dispatch(
        pushBreadcrumb({
          id,
          label: name || `Objective ${id}`,
          path: `/po/${id}`,
          type: "projectObjective",
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.info?.name, projectObjectiveId, location.pathname]);

  // Record in Recently Viewed when opened
  useEffect(() => {
    const id = data?.id || projectObjectiveId;
    if (data == null || !id || recordedIdRef.current === id) return;
    const name = data?.info?.name || "Objective";
    record({
      id: String(id),
      title: String(name || id),
      url: `/po/${id}`,
      type: "Project Objective",
    });
    recordedIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, projectObjectiveId]);

  // Keep local references only when needed. Avoid unused assignments while hidden during loading.
  const targets = data?.targets || {};
  const diversity = data?.diversity || {};
  const stats = data?.stats || {};
  const rates = data?.rates || {};
  const sys = data?.sys || {};

  const { picklistMap } = usePicklistMap(
    Object.values(PO_PICKLIST_LABELS),
    SOBJECT_PROJECT_OBJECTIVE,
  );

  // Sync objective metadata to orchestrator when data loads
  useEffect(() => {
    if (data?.info && typeof setObjectiveMetadata === "function") {
      setObjectiveMetadata(data.info);
    }
  }, [data?.info, setObjectiveMetadata]);

  useEffect(() => {
    if (isEditing) {
      setForm(data);
      setSuccess("");
    }
  }, [isEditing, data]);

  function handleChange(path, value) {
    setForm((prev) => {
      let next = setByPathCloned(prev || {}, path, value);
      if (path === "requirements.country" && value !== "" && value !== null) {
        next = setByPathCloned(
          next,
          "requirements.includeInCountryList",
          false,
        );
        next = setByPathCloned(next, "requirements.listOfCountries", []);
      }
      return next;
    });
  }

  function validateEditForm(current) {
    const errs = {};
    // Required presence (name, workType)
    const required = ["info.name", "requirements.workType"];
    required.forEach((p) => {
      const v = getByPath(current, p);
      if (v === undefined || String(v).trim() === "") {
        errs[p] = "This field is required";
      }
    });
    // Minimum contributors integer check (editable)
    const minContrib = getByPath(current, "diversity.minimumContributors");
    if (
      minContrib !== undefined &&
      minContrib !== null &&
      String(minContrib) !== ""
    ) {
      const n = Number(minContrib);
      if (!Number.isInteger(n))
        errs["diversity.minimumContributors"] = "Must be an integer";
    }
    return errs;
  }

  async function handleSave() {
    if (!form) return;
    const mergedErrors = {
      ...validateProjectObjectiveEditForm(form, false),
      ...validateEditForm(form),
    };
    if (Object.keys(mergedErrors).length > 0) {
      setError(Object.values(mergedErrors)[0] || "Validation failed");
      return;
    }
    const id = data?.id || projectObjectiveId;
    if (!id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const partial = buildMinimalPatch(data || {}, form || {}) || {};
      if (Object.keys(partial).length === 0) {
        setIsEditing(false);
        setSuccess("No changes to update");
        return;
      }
      await projectObjective.patchProjectObjective(String(id), partial);
      const fresh = await projectObjective.fetchProjectObjective(String(id));
      setData(fresh);
      setIsEditing(false);
      setSuccess("Objective updated");
    } catch (e) {
      setError(e?.message || "Failed to update objective");
    } finally {
      setSaving(false);
    }
  }

  const resetEdit = () => {
    setForm(null);
    setError("");
  };
  // Used to Display Form Validation Errors
  useEffect(() => {
    const mergedErrors = {
      ...validateProjectObjectiveEditForm(form, false),
    };
    if (Object.keys(mergedErrors).length > 0) {
      setError(Object.values(mergedErrors)[0] || "Validation failed");
    } else {
      setError("");
    }
  }, [form]);

  // Report header state to orchestrator
  useEffect(() => {
    if (typeof onHeaderStateChange === "function") {
      const hasFormErrors =
        form &&
        Object.keys({
          ...validateProjectObjectiveEditForm(form, false),
          ...validateEditForm(form),
        }).length > 0;
      onHeaderStateChange({
        saving,
        hasFormErrors: !!hasFormErrors,
        validating,
      });
    }
  }, [saving, form, validating, onHeaderStateChange]);

  if (typeof onRegisterHandlers === "function") {
    onRegisterHandlers(() => ({
      handleSave,
      handleValidate,
      resetEdit,
    }));
  }

  const validationGroups = useMemo(() => {
    const alerts = Array.isArray(validation?.alerts) ? validation.alerts : [];
    const groups = { error: [], warning: [], info: [] };
    for (const a of alerts) {
      const t = (a?.type === "warn" ? "warning" : a?.type) || "info";
      if (t === "error" || t === "warning" || t === "info") {
        groups[t].push(a);
      } else {
        groups.info.push(a);
      }
    }
    return groups;
  }, [validation]);

  async function handleValidate() {
    const id = data?.id || projectObjectiveId;
    if (!id) {
      setValidationError("Missing record id");
      return;
    }
    setValidationError("");
    setValidating(true);
    setSuccess("");
    setHideValidationSuccess(false);
    try {
      const resp = await http.sfGet(`/Validate`, {
        id: String(id),
        type: "ProjectObjective",
      });
      if (resp == null) {
        setValidation(null);
        return;
      }
      const successFlag = resp?.success === true;
      const alerts = Array.isArray(resp?.alerts) ? resp.alerts : [];
      setValidation({ success: successFlag, alerts });
    } catch (e) {
      const details = e?.details;
      if (details && typeof details === "object") {
        const alerts = Array.isArray(details.alerts) ? details.alerts : [];
        setValidation({ success: !!details.success, alerts });
      } else {
        setValidation(null);
        setValidationError(e?.message || "Validation failed");
      }
    } finally {
      setValidating(false);
    }
  }

  // Map friendly page type to PageUtils pagetype param and ESM layout component
  const previewPageTypeToApi = (label) =>
    label === "Project Pre-Qualification" ? "PreQualPage" : "ActivePage";
  const previewPageTypeToEsm = (label) =>
    PROJECT_PAGE_TYPE_TO_LAYOUT[label] || "CommonProjectPage";

  // Fetch preview payload from PageUtils
  const loadPreview = async () => {
    const poid = data?.id || projectObjectiveId;
    if (!poid) return;
    setPreviewLoading(true);
    setPreviewError("");
    setPreviewPayload(null);
    try {
      const pagetype = previewPageTypeToApi(previewPageType);
      const res = await pageUtils.fetchProjectPagePreview({
        projectObjectiveId: String(poid),
        pageType: pagetype,
      });
      setPreviewPayload(res || null);
    } catch (e) {
      setPreviewError(e?.message || "Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // When preview is toggled on (e.g. from orchestrator header), load preview payload
  useEffect(() => {
    if (showPreview && (data?.id || projectObjectiveId)) {
      loadPreview();
    }
  }, [showPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sticky Header: only when not controlled by orchestrator */}
        {!isControlled && (
          <>
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 rounded-lg p-4 mb-6 shadow-lg transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Typography.Title
                        level={5}
                        style={{ margin: 0, color: "#6b7280" }}
                      >
                        Project Objective
                      </Typography.Title>
                    </div>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, marginTop: 4 }}
                    >
                      {data?.info?.name || "Unnamed Objective"}
                    </Typography.Title>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!isEditing ? (
                    <>
                      <Button
                        type="primary"
                        onClick={() => setIsEditing(true)}
                        icon={<Edit size={16} />}
                        className="shadow-sm"
                      >
                        Edit
                      </Button>
                      <Button
                        type="default"
                        onClick={() => setShowCloneModal(true)}
                        icon={<Copy size={16} />}
                        className="shadow-sm"
                      >
                        Clone
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => {
                          setIsEditing(false);
                          setForm(null);
                        }}
                        className="shadow-sm"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleSave}
                        disabled={saving}
                        loading={saving}
                        className="shadow-sm"
                        icon={<Save size={16} />}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  )}

                  <div className="h-6 w-px bg-gray-300"></div>

                  <Button
                    type="default"
                    onClick={handleValidate}
                    loading={validating}
                    className="shadow-sm"
                  >
                    {validating ? "Validating..." : "Validate"}
                  </Button>

                  <Button
                    type={showPreview ? "primary" : "default"}
                    onClick={() => {
                      setShowPreview((v) => {
                        const next = !v;
                        if (next) loadPreview();
                        return next;
                      });
                    }}
                    icon={
                      showPreview ? <EyeOff size={16} /> : <Eye size={16} />
                    }
                    className="shadow-sm"
                  >
                    {showPreview ? "Hide Preview" : "Show Preview"}
                  </Button>
                </div>
              </div>
            </div>

            <CloneProjectObjectiveModal
              visible={showCloneModal}
              onClose={() => setShowCloneModal(false)}
              onSuccess={() => setShowCloneModal(false)}
            />
          </>
        )}

        {loading && (
          <div className="mb-6">
            <Spin fullscreen tip="Loading project objective…" />
          </div>
        )}
        {error && (
          <div className="mb-6">
            <Alert type="error" showIcon message={error} />
          </div>
        )}
        {success && (
          <div className="mb-6">
            <Alert type="success" showIcon message={success} />
          </div>
        )}
        {validationError && (
          <div className="mb-6">
            <Alert type="error" showIcon message={validationError} />
          </div>
        )}
        {validation && (
          <div className="mb-6 space-y-4">
            {Array.isArray(validation.alerts) &&
            validation.alerts.length === 0 ? (
              !hideValidationSuccess ? (
                <div
                  className="fade-out-after-3s"
                  onAnimationEnd={() => setHideValidationSuccess(true)}
                >
                  <Alert
                    type="success"
                    showIcon
                    message="Validation Successful"
                    className="border-green-200 bg-green-50"
                  />
                </div>
              ) : null
            ) : (
              <div className="space-y-4">
                {validationGroups.error.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Validation Errors (${validationGroups.error.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.error.map((a, idx) => (
                          <li key={`err-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-red-200 bg-red-50"
                  />
                )}
                {validationGroups.warning.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`Warnings (${validationGroups.warning.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.warning.map((a, idx) => (
                          <li key={`warn-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-yellow-200 bg-yellow-50"
                  />
                )}
                {validationGroups.info.length > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`Information (${validationGroups.info.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.info.map((a, idx) => (
                          <li key={`info-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-blue-200 bg-blue-50"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {data && (
          <div
            ref={containerRef}
            className={`${showPreview ? "flex gap-4" : "max-w-6xl mx-auto"} ${
              isDragging ? "select-none cursor-col-resize" : ""
            }`}
          >
            <div
              className="h-min bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/20"
              style={{
                width: showPreview ? `${previewWidth}%` : "100%",
              }}
            >
              <div className="p-6">
                <Tabs
                  defaultActiveKey="info"
                  items={[
                    {
                      key: "info",
                      label: "Info",
                      children: renderGrid(
                        [
                          {
                            label: "Salesforce Id",
                            value: data?.id || "—",
                            readOnly: true,
                          },
                          // shared info fields (includes Project field)
                          ...buildInfoFieldsForDetails({ form, data }),
                        ],
                        {
                          editing: isEditing,
                          form: form || data,
                          onChange: handleChange,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: data,
                        },
                      ),
                    },
                    {
                      key: "requirements",
                      label: "Requirements",
                      children: renderGrid(
                        resolvePicklistFields(
                          buildRequirementsFieldsForDetails({ form, data }),
                          picklistMap,
                        ),
                        {
                          editing: isEditing,
                          form: form || data,
                          onChange: handleChange,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: data,
                        },
                      ),
                    },
                    {
                      key: "funnel",
                      label: "Funnel Splits",
                      children: renderGrid(buildFunnelFieldsForDetails(), {
                        editing: isEditing,
                        form: form || data,
                        onChange: handleChange,
                        onEnableEdit: () => setIsEditing(true),
                        originalData: data,
                      }),
                    },
                    {
                      key: "targets",
                      label: "Targets",
                      children: renderGrid(
                        resolvePicklistFields(
                          buildTargetsFieldsForDetails({ form, data }),
                          picklistMap,
                        ),

                        {
                          editing: isEditing,
                          form: form || data,
                          onChange: handleChange,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: data,
                        },
                      ),
                    },
                    {
                      key: "diversity",
                      label: "Diversity",
                      children: renderGrid(buildDiversityFieldsForDetails(), {
                        editing: isEditing,
                        form: form || data,
                        onChange: handleChange,
                        onEnableEdit: () => setIsEditing(true),
                        originalData: data,
                      }),
                    },
                    {
                      key: "stats",
                      label: "Stats",
                      children: renderGrid(buildStatsFieldsForDetails(stats), {
                        onEnableEdit: null, // Read-only fields
                      }),
                    },
                    {
                      key: "rates",
                      label: "Rates",
                      children: renderGrid(buildRatesFieldsForDetails(rates), {
                        editing: isEditing,
                        form: form || data,
                        onChange: handleChange,
                        onEnableEdit: () => setIsEditing(true),
                        originalData: data,
                      }),
                    },
                    {
                      key: "sys",
                      label: "System",
                      children: renderGrid(buildSysFieldsForDetails(sys), {
                        onEnableEdit: null, // System fields are not editable
                      }),
                    },
                  ]}
                />
                {/* Project Pages Section */}
                <ProjectPagesSection
                  projectId={data?.info?.projectId}
                  projectName={data?.info?.projectName}
                  projectObjectiveId={projectObjectiveId}
                  projectObjectiveName={data?.info?.name}
                />
              </div>
            </div>
            {showPreview && (
              <>
                {/* Drag Handle */}
                <div
                  className="w-2 cursor-col-resize flex-shrink-0 transition-colors duration-200 relative group"
                  onMouseDown={() => setIsDragging(true)}
                >
                  <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bg-gray-300 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-6 rounded-full group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                <div
                  className="flex-shrink-0"
                  style={{ width: `calc(${100 - previewWidth}% - 40px)` }}
                >
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Live Preview
                      </h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Zap size={16} className="mr-2" />
                        Real-time rendering
                      </div>
                    </div>
                    <div className="rounded-lg">
                      <div className="bg-white border border-gray-200 rounded-lg">
                        <div className="w-full flex items-center justify-between p-6">
                          <div className="flex items-start flex-col">
                            <div className="text-lg font-semibold text-gray-900">
                              Preview
                            </div>
                            <div className="text-sm text-gray-600">
                              Select a page type to preview the Project Page for
                              this objective
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Select
                              options={Object.keys(
                                PROJECT_PAGE_TYPE_TO_LAYOUT,
                              ).map((key) => ({
                                label: key,
                                value: PROJECT_PAGE_TYPE_TO_LAYOUT[key],
                              }))}
                              value={selectedPreviewType}
                              onChange={(value) =>
                                setSelectedPreviewType(value)
                              }
                              className="flex-1"
                              placeholder="Select preview type"
                            />
                            <Button
                              onClick={loadPreview}
                              disabled={previewLoading}
                            >
                              {previewLoading ? "Refreshing…" : "Refresh"}
                            </Button>
                          </div>
                        </div>
                        <div className="border-t border-gray-200 p-4">
                          {previewLoading && (
                            <div className="mb-2">
                              <Spin size="small" /> Loading preview…
                            </div>
                          )}
                          {previewError && (
                            <div className="mb-2">
                              <Alert
                                type="error"
                                showIcon
                                message={previewError}
                              />
                            </div>
                          )}
                          {!previewLoading &&
                            !previewError &&
                            previewPayload && (
                              <PreviewOrchestrator
                                objectType={selectedPreviewType}
                                payload={{
                                  title: previewPayload.title,
                                  status: previewPayload.status,
                                  productivity_target_type:
                                    previewPayload.productivity_target_type,
                                  pay_rate: previewPayload.pay_rate,
                                  hours_required: previewPayload.hours_required,
                                  duration: previewPayload.duration,
                                  date_end: previewPayload.date_end,
                                  splash_image_url:
                                    previewPayload.splash_image_url,
                                  components: Array.isArray(
                                    previewPayload.components,
                                  )
                                    ? previewPayload.components.map((row) => ({
                                        ...row,
                                        type: row.type || "Headline_only",
                                      }))
                                    : [],
                                  categories: previewPayload.categories || [],
                                }}
                              />
                            )}
                          {!previewLoading &&
                            !previewError &&
                            !previewPayload && (
                              <div className="text-sm text-gray-600">
                                No preview available.
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectObjectiveDetails;
